import React, { useState, useCallback } from "react";
import {
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Activity,
} from "lucide-react";
import { colors } from "../../../constants/colors";

const DailyUsageSection = ({ uid }) => {
  const [dailyUsage, setDailyUsage] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Today's date in YYYY-MM-DD format
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [showDailyUsage, setShowDailyUsage] = useState(false);
  const [error, setError] = useState("");

  // Fetch daily usage for specific date
  const fetchDailyUsage = useCallback(
    async (date) => {
      setIsLoadingUsage(true);

      let token = sessionStorage.getItem("adminToken");
      if (!token) {
        token = localStorage.getItem("authToken");
      }
      if (!token) {
        setError("No authentication token found");
        setIsLoadingUsage(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admin/users/${uid}/daily-usage/${date}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error("Authentication failed. Please login again.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to fetch daily usage");
        }

        setDailyUsage(data.dailyUsage);
        console.log(
          `✅ Daily usage fetched for date ${date}:`,
          data.dailyUsage
        );
      } catch (err) {
        console.error("Failed to fetch daily usage:", err);
        setError(`Failed to fetch daily usage: ${err.message}`);
      } finally {
        setIsLoadingUsage(false);
      }
    },
    [uid]
  );

  // Handle daily usage toggle
  const handleDailyUsageToggle = () => {
    if (!showDailyUsage && !dailyUsage) {
      fetchDailyUsage(selectedDate);
    }
    setShowDailyUsage(!showDailyUsage);
  };

  // Handle date change
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    if (showDailyUsage) {
      fetchDailyUsage(newDate);
    }
  };

  // Styles
  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "0.5rem 0",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={handleDailyUsageToggle}>
        <div style={titleStyle}>
          <Activity size={20} />
          Daily Usage
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {showDailyUsage ? (
            <EyeOff size={16} color={colors.darkGray} />
          ) : (
            <Eye size={16} color={colors.darkGray} />
          )}
          {showDailyUsage ? (
            <ChevronUp size={20} color={colors.darkGray} />
          ) : (
            <ChevronDown size={20} color={colors.darkGray} />
          )}
          {isLoadingUsage && (
            <RefreshCw
              size={16}
              color={colors.darkGray}
              className="animate-spin"
            />
          )}
        </div>
      </div>

      {showDailyUsage && (
        <div style={{ marginTop: "1rem" }}>
          {/* Date Picker */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "600",
                color: colors.darkBlue,
              }}
            >
              Select Date:
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              style={{
                padding: "0.75rem",
                border: `2px solid ${colors.doubleExtraLightGray}`,
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none",
                backgroundColor: colors.white,
                color: colors.darkBlue,
              }}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}

          {/* Daily Usage Data */}
          {isLoadingUsage ? (
            <div
              style={{
                padding: "3rem",
                textAlign: "center",
                color: colors.darkGray,
              }}
            >
              Loading daily usage data for {selectedDate}...
            </div>
          ) : dailyUsage ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                }}
              >
                {/* Nutrition Cards */}
                <div
                  style={{
                    backgroundColor: colors.extraLightGray,
                    padding: "1rem",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.5rem",
                    }}
                  >
                    Calories
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: colors.darkGreen,
                    }}
                  >
                    {dailyUsage.calories || 0}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                    kcal
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: colors.extraLightGray,
                    padding: "1rem",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.5rem",
                    }}
                  >
                    Protein
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: colors.darkBlue,
                    }}
                  >
                    {dailyUsage.protein || 0}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                    grams
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: colors.extraLightGray,
                    padding: "1rem",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.5rem",
                    }}
                  >
                    Carbs
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#f59e0b",
                    }}
                  >
                    {dailyUsage.carbs || 0}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                    grams
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: colors.extraLightGray,
                    padding: "1rem",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.5rem",
                    }}
                  >
                    Fat
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#ef4444",
                    }}
                  >
                    {dailyUsage.fat || 0}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                    grams
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: colors.extraLightGray,
                    padding: "1rem",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.5rem",
                    }}
                  >
                    Fiber
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#10b981",
                    }}
                  >
                    {dailyUsage.fiber || 0}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                    grams
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: colors.extraLightGray,
                    padding: "1rem",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.5rem",
                    }}
                  >
                    Sodium
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#8b5cf6",
                    }}
                  >
                    {dailyUsage.sodium || 0}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                    mg
                  </div>
                </div>
              </div>

              {/* Goal Achievement Status */}
              {dailyUsage.goalAchievement && (
                <div style={{ marginTop: "1rem" }}>
                  <h4
                    style={{
                      margin: "0 0 0.75rem 0",
                      color: colors.darkBlue,
                      fontSize: "1rem",
                      fontWeight: "600",
                    }}
                  >
                    Goal Achievement Status:
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {Object.entries(dailyUsage.goalAchievement).map(
                      ([goal, achieved]) => (
                        <span
                          key={goal}
                          style={{
                            padding: "0.25rem 0.75rem",
                            borderRadius: "20px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            backgroundColor: achieved ? "#dcfce7" : "#fee2e2",
                            color: achieved ? "#166534" : "#dc2626",
                          }}
                        >
                          {goal.toUpperCase()}: {achieved ? "✓" : "✗"}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                padding: "3rem",
                textAlign: "center",
                color: colors.darkGray,
              }}
            >
              No daily usage data found for {selectedDate}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyUsageSection;
